import { ReportBrowseQuery } from '@/services/api/reportsV2';
import { ReportsURLState, ReportType } from './types';

export const defaultReportsURLState: ReportsURLState = {
  q: '', type: '', playerId: '', authorId: '', from: '', to: '', minRating: null, maxRating: null,
  medicalStatus: '', medicalSeverity: '', hasAttachment: null, sort: 'date', order: 'desc',
  page: 1, pageSize: 20, tab: 'reports',
};

const reportTypes = new Set<ReportType>(['scouting', 'performance', 'medical', 'training']);
const statuses = new Set(['fit', 'injured', 'recovering', 'questionable']);
const severities = new Set(['minor', 'moderate', 'severe']);
const sorts = new Set<ReportsURLState['sort']>(['date', 'player', 'author', 'rating']);
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const datePattern = /^\d{4}-\d{2}-\d{2}$/;

const validDate = (value: string): boolean => {
  if (!datePattern.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export const parseReportsURLState = (params: URLSearchParams): ReportsURLState => {
  const number = (name: string): number | null => {
    const value = Number(params.get(name));
    return Number.isInteger(value) && value >= 1 && value <= 100 ? value : null;
  };
  const type = params.get('type') || '';
  const status = params.get('medical_status') || '';
  const severity = params.get('medical_severity') || '';
  const sort = params.get('sort') || '';
  const order = params.get('order');
  const page = Number(params.get('page'));
  const pageSize = Number(params.get('page_size'));
  const attachment = params.get('has_attachment');
  const tab = params.get('tab');
  const playerId = params.get('player_id') || '';
  const authorId = params.get('author_id') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  let normalizedFrom = validDate(from) ? from : '';
  let normalizedTo = validDate(to) ? to : '';
  if (normalizedFrom && normalizedTo && normalizedFrom > normalizedTo) {
    normalizedFrom = '';
    normalizedTo = '';
  }
  let minRating = number('min_rating');
  let maxRating = number('max_rating');
  if (minRating !== null && maxRating !== null && minRating > maxRating) {
    minRating = null;
    maxRating = null;
  }
  return {
    q: (params.get('q') || '').trim(),
    type: reportTypes.has(type as ReportType) ? type as ReportType : '',
    playerId: uuidPattern.test(playerId) ? playerId : '',
    authorId: uuidPattern.test(authorId) ? authorId : '',
    from: normalizedFrom,
    to: normalizedTo,
    minRating, maxRating,
    medicalStatus: statuses.has(status) ? status as ReportsURLState['medicalStatus'] : '',
    medicalSeverity: severities.has(severity) ? severity as ReportsURLState['medicalSeverity'] : '',
    hasAttachment: attachment === 'true' ? true : attachment === 'false' ? false : null,
    sort: sorts.has(sort as ReportsURLState['sort']) ? sort as ReportsURLState['sort'] : 'date',
    order: order === 'asc' ? 'asc' : 'desc',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: pageSize === 50 || pageSize === 100 ? pageSize : 20,
    tab: tab === 'documents' ? 'documents' : 'reports',
  };
};

export const serializeReportsURLState = (state: ReportsURLState): URLSearchParams => {
  const params = new URLSearchParams();
  const add = (name: string, value: string | number | boolean | null) => { if (value !== '' && value !== null) params.set(name, String(value)); };
  add('q', state.q.trim()); add('type', state.type); add('player_id', state.playerId); add('author_id', state.authorId);
  add('from', state.from); add('to', state.to); add('min_rating', state.minRating); add('max_rating', state.maxRating);
  add('medical_status', state.medicalStatus); add('medical_severity', state.medicalSeverity); add('has_attachment', state.hasAttachment);
  if (state.sort !== 'date') add('sort', state.sort); if (state.order !== 'desc') add('order', state.order);
  if (state.page !== 1) add('page', state.page); if (state.pageSize !== 20) add('page_size', state.pageSize);
  if (state.tab !== 'reports') add('tab', state.tab);
  return params;
};

export const reportsStateToQuery = (state: ReportsURLState): ReportBrowseQuery => ({
  ...(state.q ? { q: state.q } : {}), ...(state.type ? { type: state.type } : {}),
  ...(state.playerId ? { playerId: state.playerId } : {}), ...(state.authorId ? { authorId: state.authorId } : {}),
  ...(state.from ? { from: state.from } : {}), ...(state.to ? { to: state.to } : {}),
  ...(state.minRating !== null ? { minRating: state.minRating } : {}), ...(state.maxRating !== null ? { maxRating: state.maxRating } : {}),
  ...(state.medicalStatus ? { medicalStatus: state.medicalStatus } : {}), ...(state.medicalSeverity ? { medicalSeverity: state.medicalSeverity } : {}),
  ...(state.hasAttachment !== null ? { hasAttachment: state.hasAttachment } : {}), sort: state.sort, order: state.order, page: state.page, pageSize: state.pageSize,
});

export const updateReportsURLState = (state: ReportsURLState, patch: Partial<ReportsURLState>, resetsDataset = true): ReportsURLState => ({ ...state, ...patch, page: resetsDataset && patch.page === undefined ? 1 : patch.page ?? state.page });
